import { useState } from "react";
import {
  RiRobot2Line,
  RiMailLine,
  RiCloseLine,
  RiRefreshLine,
  RiCheckLine,
  RiMagicLine,
} from "react-icons/ri";
import TemplatePreview from "./TemplatePreview";

const AITemplateGenerator = ({ onClose, onGenerate }) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [selectedTone, setSelectedTone] = useState("professional");
  const [selectedLength, setSelectedLength] = useState("medium");
  const [showPreview, setShowPreview] = useState(false);
  const [autoShowPreview, setAutoShowPreview] = useState(true);

  const toneOptions = [
    { id: "professional", label: "Professional" },
    { id: "friendly", label: "Friendly" },
    { id: "casual", label: "Casual" },
    { id: "formal", label: "Formal" },
    { id: "persuasive", label: "Persuasive" },
  ];

  const lengthOptions = [
    { id: "short", label: "Short", description: "50-100 words" },
    { id: "medium", label: "Medium", description: "100-200 words" },
    { id: "long", label: "Long", description: "200-300 words" },
  ];

  const promptSuggestions = [
    "Welcome email for new subscribers",
    "Monthly newsletter template",
    "Product launch announcement",
    "Special discount offer",
    "Event invitation",
    "Customer feedback request",
  ];

  const handleGenerate = async () => {
    setLoading(true);
    try {
      // Simulate API call to AI service
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock response
      const mockTemplate = {
        subject: "Welcome to Our Community! 🎉",
        content: `Dear {first_name},

We're thrilled to welcome you to our community! Thank you for joining us on this exciting journey.

Here's what you can expect:
• Weekly updates on industry trends
• Exclusive member-only content
• Special offers and early access

Get started by checking out our latest resources at {resource_link}.

Best regards,
{sender_name}
{company_name}`,
        variables: [
          "first_name",
          "resource_link",
          "sender_name",
          "company_name",
        ],
        tone: selectedTone,
        category: "marketing",
        tags: ["welcome", "onboarding"],
      };

      setGeneratedTemplate(mockTemplate);
      if (autoShowPreview) {
        setShowPreview(true);
      }
    } catch (error) {
      console.error("Error generating template:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center space-x-3">
            <RiRobot2Line className="w-6 h-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-secondary-900">
              AI Template Generator
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-50 rounded-lg"
          >
            <RiCloseLine className="w-6 h-6 text-secondary-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Prompt Input */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Describe your email template
              </label>
              <textarea
                className="input-field h-24"
                placeholder="E.g., Generate a welcome email for new subscribers that introduces our main features and includes a getting started guide..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />

              {/* Prompt Suggestions */}
              <div className="mt-3">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Quick Suggestions
                </label>
                <div className="flex flex-wrap gap-2">
                  {promptSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setPrompt(suggestion)}
                      className="px-3 py-1 bg-secondary-50 hover:bg-secondary-100 text-secondary-700 rounded-full text-sm"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tone Selection */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Tone of Voice
              </label>
              <div className="flex flex-wrap gap-2">
                {toneOptions.map((tone) => (
                  <button
                    key={tone.id}
                    onClick={() => setSelectedTone(tone.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      selectedTone === tone.id
                        ? "bg-primary-50 text-primary-600 border-2 border-primary-500"
                        : "bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                    }`}
                  >
                    {tone.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Length Selection */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Email Length
              </label>
              <div className="flex flex-wrap gap-2">
                {lengthOptions.map((length) => (
                  <button
                    key={length.id}
                    onClick={() => setSelectedLength(length.id)}
                    className={`px-4 py-2 rounded-lg text-sm ${
                      selectedLength === length.id
                        ? "bg-primary-50 text-primary-600 border-2 border-primary-500"
                        : "bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                    }`}
                  >
                    <span className="font-medium">{length.label}</span>
                    <span className="text-xs block text-secondary-500">
                      {length.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview Settings */}
            <div className="flex items-center justify-between py-2 border-t border-secondary-200">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-secondary-700">
                  Auto-show preview
                </span>
                <button
                  onClick={() => setAutoShowPreview(!autoShowPreview)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoShowPreview ? "bg-primary-500" : "bg-secondary-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoShowPreview ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
              {generatedTemplate && !showPreview && (
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                >
                  <RiMailLine className="w-4 h-4 mr-1" />
                  Show Preview
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-secondary-200 bg-white">
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-secondary-700 hover:bg-secondary-50 rounded-lg"
            >
              Cancel
            </button>
            <div className="flex space-x-3">
              {generatedTemplate && (
                <button
                  onClick={handleGenerate}
                  className="flex items-center px-4 py-2 text-secondary-700 hover:bg-secondary-50 rounded-lg"
                >
                  <RiRefreshLine className="w-5 h-5 mr-2" />
                  Regenerate
                </button>
              )}
              <button
                onClick={
                  generatedTemplate
                    ? () => onGenerate(generatedTemplate)
                    : handleGenerate
                }
                disabled={!prompt || loading}
                className={`px-4 py-2 rounded-lg flex items-center justify-center min-w-[120px] ${
                  generatedTemplate
                    ? "bg-primary-600 text-white hover:bg-primary-700"
                    : "bg-primary-500 text-white hover:bg-primary-600"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                ) : generatedTemplate ? (
                  <>
                    <RiCheckLine className="w-5 h-5 mr-2" />
                    Use Template
                  </>
                ) : (
                  <>
                    <RiMagicLine className="w-5 h-5 mr-2" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && generatedTemplate && (
        <TemplatePreview
          template={generatedTemplate}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default AITemplateGenerator;
